import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {deleteRecord, fetchRecords} from '../services/InvoiceItemsService';
import {useState, useEffect} from 'react';
import {InvoiceItem} from '../types';
import {usePagination} from '../../../hooks/usePagination';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const useInvoiceItems = (invoiceId: string) => {

    if (typeof invoiceId === 'undefined') {
        throw new Error('invoiceId is required parameter')
    }

    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const pagination = usePagination();

    const invoiceItemsQuery = useQuery({
        queryKey: ['invoiceItems', invoiceId, pagination.currentToken],
        queryFn: () => fetchRecords({invoice: invoiceId}, pagination.pageSize, pagination.currentToken),
    });

    useEffect(() => {
        if (invoiceItemsQuery.data) pagination.setNextToken(invoiceItemsQuery.data.nextToken);
    }, [invoiceItemsQuery.data]);

    useEffect(() => {
        pagination.resetPagination();
    }, [invoiceId]);

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(invoiceId, id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Invoice item deleted');
            queryClient.invalidateQueries({queryKey: ['invoiceItems', invoiceId]});
        },
        onError: notifyError,
    });

    const fetchRecordsAction = () => {
        queryClient.invalidateQueries({queryKey: ['invoiceItems', invoiceId]});
    };

    const deleteRecords = (ids?: string[]) => {
        if (ids && ids.length > 0) {
            deleteMutation.mutate(ids);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems: InvoiceItem[] = invoiceItemsQuery.data?.items || [];

    const visibleItems = keyword
        ? allItems.filter(item => {
            const search = keyword.toLowerCase();
            return (
                item.id?.toLowerCase().includes(search) ||
                item.customerId?.toLowerCase().includes(search) ||
                item.description?.toLowerCase().includes(search) ||
                item.currency?.toLowerCase().includes(search)
            );
        })
        : allItems;

    return {
        items: allItems,
        visibleItems,
        loading: invoiceItemsQuery.isLoading,
        keyword,
        visibleCount: visibleItems.length,
        totalCount: allItems.length,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation,
        currentPage: pagination.currentPage,
        hasNext: pagination.hasNext,
        hasPrevious: pagination.hasPrevious,
        goToNextPage: pagination.goToNextPage,
        goToPreviousPage: pagination.goToPreviousPage,
    };
};
